import { Box, Typography, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { AnalyticsService, EVENTS } from '@/services';
import { Certificate, CreditType } from '@/types';

import { SearchBar } from './SearchBar';

export const FiltersOld = ({
  credits,
  selectedFilter,
  setSelectedFilter,
  setSearchTerm,
}: {
  credits?: Certificate[];
  selectedFilter: CreditType | 'all';
  setSelectedFilter: (filter: CreditType | 'all') => void;
  setSearchTerm: (term: string) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const allFilterRef = useRef<HTMLDivElement>(null);
  const lumiqFilterRef = useRef<HTMLDivElement>(null);
  const customFilterRef = useRef<HTMLDivElement>(null);

  const [allFilterWidth, setAllFilterWidth] = useState<number | undefined>(0);
  const [allFilterLeft, setAllFilterLeft] = useState<number | undefined>(0);

  const creditsByType = credits?.reduce(
    (acc, credit) => {
      acc[credit.type] = (acc[credit.type] || 0) + 1;
      return acc;
    },
    {} as Record<CreditType, number>,
  );

  const updateSize = useEffectEvent(() => {
    setAllFilterWidth(
      selectedFilter === 'all'
        ? allFilterRef.current?.offsetWidth
        : selectedFilter === 'lumiq'
          ? lumiqFilterRef.current?.offsetWidth
          : customFilterRef.current?.offsetWidth,
    );
    setAllFilterLeft(
      selectedFilter === 'all'
        ? allFilterRef.current?.offsetLeft
        : selectedFilter === 'lumiq'
          ? lumiqFilterRef.current?.offsetLeft
          : customFilterRef.current?.offsetLeft,
    );
  });

  useEffect(() => {
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    updateSize();
  }, [selectedFilter]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          p: '6px',
          bgcolor: 'grey50',
          borderRadius: 2,
          position: 'relative',
          width: {
            xs: '100%',
            sm: 'fit-content',
          },
        }}>
        <Box
          ref={allFilterRef}
          sx={[
            styles.container,
            {
              width: {
                xs: selectedFilter === 'all' ? '33%' : '30%',
                sm: 'fit-content',
              },
            },
          ]}
          onClick={() => {
            setSelectedFilter('all');
            AnalyticsService.logEvent(
              EVENTS.TRACKER_EVENTS.SOURCE_FILTER_CHANGED,
              {
                source: 'All',
              },
            );
          }}>
          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: '50%',
              bgcolor: 'grey150',
            }}
          />
          <Typography
            variant="body3SemiBold"
            sx={{
              display: {
                xs: selectedFilter === 'all' ? 'block' : 'none',
                sm: 'block',
              },
            }}>
            All
          </Typography>
          <Box sx={styles.pill}>
            <Typography variant="caption1">
              {(credits?.length ?? 0) > 999 ? '999+' : (credits?.length ?? 0)}
            </Typography>
          </Box>
        </Box>
        <Box
          ref={lumiqFilterRef}
          sx={[
            styles.container,
            {
              width: {
                xs:
                  selectedFilter === 'lumiq'
                    ? '40%'
                    : selectedFilter === 'all'
                      ? '33%'
                      : '30%',
                sm: 'fit-content',
              },
            },
          ]}
          onClick={() => {
            setSelectedFilter('lumiq');
            AnalyticsService.logEvent(
              EVENTS.TRACKER_EVENTS.SOURCE_FILTER_CHANGED,
              {
                source: 'LumiQ',
              },
            );
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
            }}>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
              }}
            />
            <Typography
              variant="body3SemiBold"
              sx={{
                display: {
                  xs: selectedFilter === 'lumiq' ? 'block' : 'none',
                  sm: 'block',
                },
              }}>
              LumiQ
            </Typography>
          </Box>
          <Box sx={styles.pill}>
            <Typography variant="caption1">
              {(creditsByType?.lumiq ?? 0) > 999
                ? '999+'
                : (creditsByType?.lumiq ?? 0)}
            </Typography>
          </Box>
        </Box>
        <Box
          ref={customFilterRef}
          sx={[
            styles.container,
            {
              width: {
                xs:
                  selectedFilter === 'custom'
                    ? '40%'
                    : selectedFilter === 'all'
                      ? '33%'
                      : '30%',
                sm: 'fit-content',
              },
            },
          ]}
          onClick={() => {
            setSelectedFilter('custom');
            AnalyticsService.logEvent(
              EVENTS.TRACKER_EVENTS.SOURCE_FILTER_CHANGED,
              {
                source: 'External',
              },
            );
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
            }}>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: 'gold',
              }}
            />
            <Typography
              variant="body3SemiBold"
              sx={{
                display: {
                  xs: selectedFilter === 'custom' ? 'block' : 'none',
                  sm: 'block',
                },
              }}>
              External
            </Typography>
          </Box>
          <Box sx={styles.pill}>
            <Typography variant="caption1">
              {(creditsByType?.custom ?? 0) > 999
                ? '999+'
                : (creditsByType?.custom ?? 0)}
            </Typography>
          </Box>
        </Box>
        {allFilterRef.current &&
          lumiqFilterRef.current &&
          customFilterRef.current && (
            <Box
              sx={{
                position: 'absolute',
                top: allFilterRef.current.offsetTop,
                left: allFilterLeft,
                width: allFilterWidth,
                height: allFilterRef.current?.offsetHeight,
                border: theme => `1.5px solid ${theme.palette.secondary.main}`,
                zIndex: 0,
                pointerEvents: 'none',
                borderRadius: 1,
                transition: isMobile ? 'none' : 'left 0.3s ease',
              }}
            />
          )}
      </Box>
      <Box
        sx={{
          height: 45,
          display: 'flex',
          alignItems: 'center',
          width: { xs: '100%', sm: 'fit-content' },
        }}>
        <SearchBar setSearchTerm={setSearchTerm} />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.25,
    borderRadius: '6px',
    border: '1.5px solid transparent',
    px: 2,
    py: 0.5,
    bgcolor: 'white',
    cursor: 'pointer',
    userSelect: 'none',
  },
  pill: {
    bgcolor: 'secondary.medium',
    px: 1,
    minWidth: 30,
    height: 20,
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
